import { routing } from '@/i18n/routing';

const { defaultLocale, localePrefix } = routing;

type Locale = (typeof routing.locales)[number];

// Locale 规范化映射：短代码 -> 完整代码（用于 API header）
export const localeNormalizeMap: Record<string, string> = {
  'hi': 'hi-IN',
  'en': 'en-US',
  'ur': 'ur-PK',
  'pt': 'pt-BR',
  'es': 'es-MX',
  // 完整代码映射到自己
  'hi-IN': 'hi-IN',
  'en-US': 'en-US',
  'ur-PK': 'ur-PK',
  'pt-BR': 'pt-BR',
  'es-MX': 'es-MX',
};

const prefixToLocaleMap = new Map<string, Locale>();
if (
  localePrefix &&
  typeof localePrefix === 'object' &&
  'prefixes' in localePrefix &&
  typeof localePrefix.prefixes === 'object'
) {
  const prefixMap = localePrefix.prefixes;
  for (const [locale, prefix] of Object.entries(prefixMap)) {
    // 在 as-needed 模式下，默认语言不应该有前缀映射
    // 因为默认语言的路径不包含前缀
    if (prefix && locale) {
      // 如果同一个 prefix 对应多个 locale（如 /hi 对应 hi 和 hi-IN），
      // 优先使用完整的 locale 代码
      const existingLocale = prefixToLocaleMap.get(prefix);
      if (!existingLocale || locale.includes('-')) {
        prefixToLocaleMap.set(prefix, locale as Locale);
      }
    }
  }
}

/**
 * 从 URL 路径中提取语言代码（locale）。
 * 支持 as-needed 模式：默认语言的路径不包含前缀
 *
 * 注意：始终返回完整的 locale 代码（如 hi-IN），因为这是后端 API 需要的格式。
 * 即使 URL 中使用短代码（如 /hi），也会规范化为完整代码（hi-IN）。
 *
 * 例如:
 * - '/hi/contact' -> 'hi-IN' (规范化为完整代码)
 * - '/en/products' -> 'en-US' (规范化为完整代码)
 * - '/' -> 默认语言的完整代码
 *
 * @param pathname URL 路径名
 * @returns 规范化后的完整语言代码
 */
export const findLocale = (pathname: string): string => {
  // 根据 prefixToLocaleMap 的键，创建一个正则表达式来匹配所有支持的前缀
  const prefixes = Array.from(prefixToLocaleMap.keys()).join('|');
  const prefixRegex = new RegExp(`^(${prefixes})`);

  const match = pathname.match(prefixRegex);

  if (match && match[1]) {
    // 找到匹配的前缀，通过 Map 获取对应的语言代码
    const prefix = match[1];
    const locale = prefixToLocaleMap.get(prefix);

    if (locale) {
      // 规范化为完整的 locale 代码（用于 API header）
      return localeNormalizeMap[locale] || locale;
    }
  }

  // 如果没有匹配到语言前缀，则返回规范化后的默认语言
  return localeNormalizeMap[defaultLocale] || defaultLocale;
};